import React from 'react';
import { theme } from '../../../../../../common/styles/theme';
import { Box, CircularProgress, IconButton, Stack, SxProps, Tooltip } from '@mui/material';

// A second, narrower segment on the right of the button (a split button), e.g. a blur toggle.
export interface ITrayButtonSide {
	title: string;
	icon: React.ReactNode;
	active?: boolean;
	pending?: boolean;
	onClick: () => void;
}

interface ITrayButton {
	className?: string;
	children: any;
	label: React.ReactNode;
	bubble?: boolean;
	orange?: boolean;
	green?: boolean;
	// no background, e.g. the recording indicator
	plain?: boolean;
	// mobile: narrower, so the whole tray fits a phone width
	compact?: boolean;
	disabled?: boolean;
	sx?: SxProps;
	onClick: () => void;
	side?: ITrayButtonSide;
}

const SIDE_WIDTH = 5;

export const TrayButton = ({
	className = '',
	children,
	label,
	bubble,
	orange,
	green,
	plain,
	compact,
	disabled,
	sx,
	onClick,
	side,
}: ITrayButton) => {
	const size = compact ? 7 : 8;

	return (
		<Stack
			className={`tray-button ${orange ? 'orange' : ''} ${green ? 'green' : ''} ${plain ? 'plain' : ''} ${disabled ? 'disabled' : ''} ${className}`}
			alignItems={'center'}
			gap={1}
			sx={{
				...sx,
				width: theme.spacing(side ? size + SIDE_WIDTH : size),
				userSelect: 'none',
				color: theme.palette.grey[500],
			}}
		>
			<Stack
				direction={'row'}
				sx={{
					borderRadius: theme.spacing(.75),
					overflow: 'hidden',
				}}
			>
			<IconButton
				onClick={onClick}
				disabled={disabled}
				sx={{
					width: theme.spacing(size),
					height: theme.spacing(size),
					background: theme.palette.grey[100],
					borderRadius: 0,
					transition: 'all .3s ease',

					// hover is on the whole tray button (the label below is clickable too), except its side segment
					'.tray-button:not(.disabled):hover:not(:has(.tray-button-side:hover)) &, &:focus, &:active': {
						background: theme.palette.grey[200],
					},

					'&.Mui-disabled': {
						background: theme.palette.grey[50],
					},

					'svg': {
						width: theme.spacing(3),
						height: theme.spacing(3),
					},

					'.tray-button.orange &': {
						color: theme.palette.common.white,
						background: theme.palette.error.main,
					},
					'.tray-button.orange:hover:not(:has(.tray-button-side:hover)) &, .tray-button.orange &:focus, .tray-button.orange &:active': {
						background: theme.palette.error.dark,
					},

					'.tray-button.green &': {
						color: theme.palette.common.white,
						background: theme.palette.grey[500],
					},
					'.tray-button.green:hover:not(:has(.tray-button-side:hover)) &, .tray-button.green &:focus, .tray-button.green &:active': {
						background: theme.palette.grey[600],
					},

					'.tray-button.plain &': {
						background: 'none',
					},
					'.tray-button.plain:not(.disabled):hover &, .tray-button.plain &:focus, .tray-button.plain &:active': {
						background: theme.palette.grey[50],
					},

				}}
			>
				{children}
			</IconButton>

			{side &&
				<Tooltip title={side.title}>
					<IconButton
						className="tray-button-side"
						aria-label={side.title}
						aria-pressed={!!side.active}
						aria-busy={!!side.pending}
						disabled={disabled}
						// not `disabled` while pending: MUI's Tooltip warns about a disabled child
						onClick={() => { if (!side.pending) side.onClick(); }}
						sx={{
							width: theme.spacing(SIDE_WIDTH),
							height: theme.spacing(size),
							p: 0,
							borderRadius: 0,
							borderLeft: `1px solid ${theme.palette.grey[300]}`,
							background: theme.palette.grey[100],
							color: side.active ? theme.palette.primary.main : theme.palette.grey[500],
							transition: 'all .3s ease',

							'&:hover': {
								background: theme.palette.grey[200],
							},

							'&.Mui-disabled': {
								background: theme.palette.grey[100],
							},

							// 24px, same as the svg's own size: at other sizes the icon gets clipped
							'svg:not(.MuiCircularProgress-svg)': {
								width: theme.spacing(3),
								height: theme.spacing(3),
							},
						}}
					>
						{side.pending ? <CircularProgress size={16} /> : side.icon}
					</IconButton>
				</Tooltip>
			}
			</Stack>

			<Box
				className="tray-button-label"
				onClick={disabled ? undefined : onClick}
				sx={{
					cursor: disabled ? 'default' : 'pointer',
					textAlign: 'center',
					fontSize: theme.typography.subtitle1,
					lineHeight: 1.2,
					wordBreak: 'break-word',

					display: '-webkit-box',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					WebkitLineClamp: 3,
					WebkitBoxOrient: 'vertical',
					// fixed, not max: a short label («Mic off») keeps the tray from jumping
					height: '3.6em',
				}}
			>
				{label}
			</Box>

		</Stack>
	);
};
